import {
  Account,
  AccountProxyType,
  accountsAtom,
  Client,
  isDefined,
  SelectedAccount,
  SelectedAccountOrClient,
} from '@eih-netbroker/common';
import { useAtomValue } from 'jotai';
import { useCallback, useContext, useMemo } from 'react';
import { AccountContext } from '../../context/AccountContext';

export const useAccounts = () => {
  const accounts = useAtomValue(accountsAtom);
  const accountContext = useContext(AccountContext);

  if (accountContext === undefined) {
    throw new Error(`useAccounts must be used within an AccountProvider`);
  }

  const selectedAccountOrClient = useMemo(
    () => accountContext.selectedAccountOrClient,
    [accountContext.selectedAccountOrClient],
  );

  const selectedAccount = useMemo<SelectedAccount | undefined>(() => {
    return isDefined(selectedAccountOrClient?.account) ? selectedAccountOrClient : undefined;
  }, [selectedAccountOrClient]);

  const isMultipleAccountSelected = useMemo<boolean>(
    () => !!(selectedAccountOrClient && selectedAccountOrClient.client !== undefined),
    [selectedAccountOrClient],
  );

  const isSelectedClientSingleAccount = useMemo<boolean>(
    () =>
      !!(
        selectedAccountOrClient &&
        !selectedAccountOrClient.client &&
        accounts.isLoaded &&
        accounts.clients.find(client =>
          client.accounts.some(account => account.accountNumber === selectedAccountOrClient?.account?.accountNumber),
        )?.accounts.length === 1
      ),
    [selectedAccountOrClient, accounts],
  );

  const selectedClient = useMemo<Client | undefined>(() => {
    if (!accounts.isLoaded) {
      return undefined;
    }
    return selectedAccountOrClient?.client
      ? accounts.clients.find(client => client.clientId === selectedAccountOrClient.client)
      : accounts.clients.find(client =>
          client.accounts.some(account => account.accountNumber === selectedAccountOrClient?.account?.accountNumber),
        );
  }, [accounts, selectedAccountOrClient]);

  const selectedClientName = useMemo(() => selectedClient?.clientName, [selectedClient]);

  /**
   * When a client is selected returns every account of the client, otherwise returns the selected account as an array
   */
  const selectedAccountList: Array<Account> = useMemo(() => {
    if (!selectedAccountOrClient || !accounts.isLoaded) {
      return [];
    }
    if (selectedAccountOrClient.account) {
      return [selectedAccountOrClient.account];
    }
    return accounts.clients.find(client => client.clientId === selectedAccountOrClient.client)?.accounts || [];
  }, [accounts, selectedAccountOrClient]);

  const getAccountById = useCallback(
    (accountNumber: string): Account => {
      if (!accounts.isLoaded) {
        throw new Error('Accounts are not loaded');
      }
      const clients = accounts.clients;

      const account = clients
        .find(client => client.accounts.some(account => account.accountNumber === accountNumber))
        ?.accounts.find(account => account.accountNumber === accountNumber);
      if (!account) {
        throw new Error(`Account not found: ${accountNumber}`);
      }

      return account;
    },
    [accounts],
  );

  const getMainClientId = useCallback(() => {
    if (!accounts.isLoaded) {
      throw new Error('Accounts are not loaded');
    }
    const clients = accounts.clients;

    const mainClientId = clients.find(client => client.accounts.some(a => a.accountProxyType === AccountProxyType.Main))
      ?.clientId;

    if (!mainClientId) {
      throw new Error(`Main client not found`);
    }

    return mainClientId;
  }, [accounts]);

  const getAccountType = useCallback(
    (selectedAccountOrClient: SelectedAccountOrClient): AccountProxyType => {
      if (!accounts.isLoaded) {
        throw new Error('Accounts are not loaded');
      }
      if (selectedAccountOrClient.account) {
        return selectedAccountOrClient.account.accountProxyType;
      } else if (selectedAccountOrClient.client) {
        if (selectedAccountOrClient.accounts.length > 0) {
          return selectedAccountOrClient.accounts[0].accountProxyType;
        }
      }

      throw new Error('No accounts found for the selected client');
    },
    [accounts],
  );

  const getMainClientIdBySelectedAccount = useCallback(
    (selectedAccountOrClient: SelectedAccountOrClient) => {
      if (!accounts.isLoaded) {
        throw new Error('Accounts are not loaded');
      }

      const accountType = getAccountType(selectedAccountOrClient);
      if (accountType === AccountProxyType.Sub) {
        return getMainClientId();
      }

      return selectedAccountOrClient.account
        ? selectedAccountOrClient.account.clientId
        : selectedAccountOrClient.client;
    },
    [accounts.isLoaded, getAccountType, getMainClientId],
  );

  return {
    selectedAccountOrClient: accountContext.selectedAccountOrClient as SelectedAccountOrClient,
    selectedAccount,
    getSelectedAccount: accountContext.getSelectedAccount,
    initAccountSelectorButton: accountContext.initAccountSelectorButton,
    openAccountSelector: accountContext.openAccountSelector,
    selectAccount: accountContext.selectAccount,
    selectClient: accountContext.selectClient,
    getAccountsOfClient: accountContext.getAccountsOfClient,
    getAccountById,
    getMainClientId,
    isSelectedClientSingleAccount,
    isAccountSelectorOpen: accountContext.isAccountSelectorOpen,
    isMultipleAccountSelected,
    accounts,
    selectedClient,
    selectedClientName,
    selectedAccountList,
    getMainClientIdBySelectedAccount,
  };
};
